import { BaseTypography } from '@/components/common/baseText/BaseTypography';
import {
    getPromotionTool,
    PromotionToolEnum,
} from '@/components/images/base64/PromotionTool';
import { AudioPlayContext } from '@/contexts/AudioPlayContext';
import { BalanceContext } from '@/contexts/BalanceContext';
import { GaContext } from '@/contexts/GaContext';
import { usePromotionTool } from '@/hooks/popup/promotiontool/usePromotionTool';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { AnyAction, Dispatch } from '@reduxjs/toolkit';
import {
    RefObject,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { WebSocketContext } from '../../../../contexts/WebSocketContext';
import { CMDPsSuspendPromotionPopup } from '../../../../models/cmd/live';
import { RootState } from '../../../../store/store';
import { numberFormat } from '../../../../utils/commonFunc';
import { getPromotionToolState } from '../../../main/selector';
import { promotionToolSliceActions } from '../../../main/slice/promotiontool/promotionTool';
import { popupSliceActions } from '../../slice';
import { promotionMoneyFigure } from '../PromotionObjectMapper';
import {
    dateToString,
    dateToTimeLeftArray,
    getDisplayTimeZone,
    getImageJsonArray,
    ImageType,
    timeToString,
} from '../PromotionUtil';
import {
    EventChecked,
    EventClock0,
    EventClock1,
    EventClock2,
    EventClock3,
    EventClock4,
    EventClock5,
    EventClock6,
    EventClock7,
    EventClock8,
    EventClock9,
    EventClockColon,
} from '../svg';
import { PromotionEventType, PromotionPage } from './PromotionDialog';

export type PromotionClockProps = {
    str: string;
    type: PromotionEventType;
};
export const PromotionClock = (props: PromotionClockProps) => {
    const color =
        props.type === PromotionEventType.Promotion ? 'promotion' : '';
    const size =
        props.type === PromotionEventType.Leaderboard ? 'leaderboard' : '';
    switch (props.str) {
        case 'event_clock_0':
            return <EventClock0 className={`clock ${color} figure ${size}`} />;
        case 'event_clock_1':
            return <EventClock1 className={`clock ${color} figure ${size}`} />;
        case 'event_clock_2':
            return <EventClock2 className={`clock ${color} figure ${size}`} />;
        case 'event_clock_3':
            return <EventClock3 className={`clock ${color} figure ${size}`} />;
        case 'event_clock_4':
            return <EventClock4 className={`clock ${color} figure ${size}`} />;
        case 'event_clock_5':
            return <EventClock5 className={`clock ${color} figure ${size}`} />;
        case 'event_clock_6':
            return <EventClock6 className={`clock ${color} figure ${size}`} />;
        case 'event_clock_7':
            return <EventClock7 className={`clock ${color} figure ${size}`} />;
        case 'event_clock_8':
            return <EventClock8 className={`clock ${color} figure ${size}`} />;
        case 'event_clock_9':
            return <EventClock9 className={`clock ${color} figure ${size}`} />;
        case 'event_clock_colon':
            return (
                <EventClockColon className={`clock ${color} colon ${size}`} />
            );
    }
};

export const PromotionSplash = () => {
    const { playButtonAudio } = useContext(AudioPlayContext);
    const { isLeaderboard, eventConcluded, eventEnded } = usePromotionTool();
    const dispatch: Dispatch<AnyAction> = useDispatch();
    const { sendCommand } = useContext(WebSocketContext);
    const [showToday, setshowToday] = useState(false);
    const showTodayRef = useRef(false);
    const { t } = useTranslation();
    const ref = useRef<HTMLDivElement>(null);
    const [adjScale, setAdjScale] = useState(1);
    const [time, setTime] = useState<string[]>(['']);
    const { EventType, StartTimestamp, EndTimestamp, Timezone, BudgetAmount } =
        useSelector((state: RootState) => getPromotionToolState(state));
    //Currency handling
    const { currency } = useContext(BalanceContext);
    const displayTimezone = getDisplayTimeZone(Timezone);

    useEffect(() => {
        const updateTimer = () =>
            setTime(
                getImageJsonArray(
                    ImageType.Countdown,
                    dateToTimeLeftArray(EndTimestamp, displayTimezone).join(':')
                )
            );
        updateTimer();
        const timer = setInterval(() => {
            updateTimer();
        }, 1000);
        dispatch(promotionToolSliceActions.isLoading(false));
        adjustScale(ref, 443);
        return () => {
            clearInterval(timer);
            if (showTodayRef.current === true) {
                let cmd: CMDPsSuspendPromotionPopup =
                    new CMDPsSuspendPromotionPopup();
                sendCommand(cmd);
            }
        };
    }, []);
    const numbers: string[] = getImageJsonArray(
        ImageType.Prize,
        `${numberFormat(BudgetAmount, 0)}`
    );
    const periodText = useMemo<string>(() => {
        const startDate = new Date(StartTimestamp * 1000);
        const startPeriod = `${dateToString(startDate, false, '-')} ${timeToString(startDate, false, ':')}`;
        const endDate = new Date(EndTimestamp * 1000);
        const endPeriod = `${dateToString(endDate, false, '-')} ${timeToString(endDate, false, ':')}`;
        return `${startPeriod} - ${endPeriod}`;
    }, [StartTimestamp, EndTimestamp]);

    const getSnoozeButtonStyle = (checked: boolean) => {
        if (checked) {
            return `check-box ${isLeaderboard() ? 'checked-leaderboard' : 'checked'}`;
        } else {
            return 'check-box';
        }
    };

    const show = () => {
        playButtonAudio();
        setshowToday(prev => !prev);
        showTodayRef.current = !showTodayRef.current;
    };

    const adjustScale = (ref: RefObject<HTMLDivElement>, maxWidth: number) => {
        if (ref && ref.current) {
            if (ref.current.clientWidth > maxWidth) {
                setAdjScale(maxWidth / ref.current.clientWidth);
            }
        }
    };

    const { page } = useContext(GaContext);

    return (
        <>
            {isLeaderboard() && eventEnded() ? (
                <Box className="splash-event-ended">
                    <Typography className="notice-text">
                        {t('leaderboard.event_ended')}
                    </Typography>
                    <Typography className="notice-text">
                        {t('leaderboard.thank_you')}
                    </Typography>
                </Box>
            ) : (
                <Box
                    className={`total-prize-container ${isLeaderboard() ? 'leaderboard' : ''}`}
                >
                    {!isLeaderboard() && (
                        <>
                            <Box className="event-total-prize-bg">
                                {getPromotionTool(
                                    PromotionToolEnum.event_total_prize_bg
                                )}
                            </Box>
                            <Box className="total-prize-title-container">
                                <Typography className="total-prize-title-text">
                                    {`${t('promotion_tool.total_prize')} (${currency})`}
                                </Typography>
                            </Box>
                            <Stack
                                direction={'row'}
                                alignItems={'center'}
                                justifyContent={'center'}
                                className="total-prize"
                            >
                                <Box ref={ref} sx={{ display: 'inline-flex' }}>
                                    {numbers.map((value, index) => (
                                        // <BaseImage
                                        //     className={value}
                                        //     scale={adjScale}
                                        //     key={`event-total-prize-${index}`}
                                        // />
                                        <Box
                                            key={`event-total-prize-${index}`}
                                            sx={{ scale: adjScale }}
                                        >
                                            {getPromotionTool(
                                                promotionMoneyFigure[value]
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                            </Stack>
                        </>
                    )}
                    <Stack
                        direction={'row'}
                        alignItems={'center'}
                        justifyContent={'center'}
                        spacing={2}
                        className={`event-date ${isLeaderboard() ? 'leaderboard' : ''}`}
                    >
                        {/* <BaseImage
                        className="promotion_tool event_caption_arrow"
                        scale={1}
                    /> */}
                        <Box display={'flex'}>
                            {getPromotionTool(
                                PromotionToolEnum.event_caption_arrow
                            )}
                        </Box>
                        <Typography>{`${t('promotion_tool.event_date')} (UTC${displayTimezone >= 0 ? '+' + displayTimezone.toString() : displayTimezone.toString()})`}</Typography>
                        {/* <BaseImage
                        className="promotion_tool event_caption_arrow rotate180"
                        scale={1}
                    /> */}
                        <Box display={'flex'} className="rotate180">
                            {getPromotionTool(
                                PromotionToolEnum.event_caption_arrow
                            )}
                        </Box>
                    </Stack>
                    <Box
                        className={`event-period ${isLeaderboard() ? 'leaderboard' : ''}`}
                    >
                        {periodText}
                    </Box>
                    <Stack
                        direction={'row'}
                        alignItems={'center'}
                        justifyContent={'center'}
                        className={`event-remain-time ${isLeaderboard() ? 'leaderboard' : ''}`}
                        spacing={isLeaderboard() ? 0 : 1}
                    >
                        {time.map((value, index) => (
                            <PromotionClock
                                key={`event-remain-time-${index}`}
                                str={value}
                                type={EventType}
                            />
                        ))}
                    </Stack>

                    <Stack
                        className={`event-dhms ${isLeaderboard() ? 'leaderboard' : ''}`}
                    >
                        <Typography
                            className={`time-text ${isLeaderboard() ? 'leaderboard' : ''}`}
                        >
                            {t('promotion_tool.days')}
                        </Typography>
                        <Typography
                            className={`time-text ${isLeaderboard() ? 'leaderboard' : ''}`}
                        >
                            {t('promotion_tool.hours')}
                        </Typography>
                        <Typography
                            className={`time-text ${isLeaderboard() ? 'leaderboard' : ''}`}
                        >
                            {t('promotion_tool.minutes')}
                        </Typography>
                        <Typography
                            className={`time-text ${isLeaderboard() ? 'leaderboard' : ''}`}
                        >
                            {t('promotion_tool.seconds')}
                        </Typography>
                    </Stack>
                </Box>
            )}
            <Stack className="page-buttons" direction={'row'} spacing={'20px'}>
                {!isLeaderboard() && (
                    <Button
                        id={`${page}_re_popup_explore-rewards`}
                        className="button-gold"
                        onClick={() => {
                            playButtonAudio();
                            if (eventConcluded()) {
                                dispatch(
                                    promotionToolSliceActions.isOpenDialog(
                                        false
                                    )
                                );
                                dispatch(
                                    popupSliceActions.open(
                                        'promotion_tool.popup_event_ended'
                                    )
                                );
                                return;
                            }

                            dispatch(
                                promotionToolSliceActions.updatePage(
                                    PromotionPage.Gacha
                                )
                            );
                        }}
                    >
                        <BaseTypography
                            resize={{
                                direction: 'horizontal',
                                value: '200px',
                            }}
                            sx={{
                                fontSize: '20px',
                            }}
                        >
                            {t('promotion_tool.explore_rewards')}
                        </BaseTypography>
                    </Button>
                )}
                <Button
                    id={`${page}_${isLeaderboard() ? 'le' : 're'}_popup_rankings`}
                    className={
                        !isLeaderboard()
                            ? 'button-black'
                            : 'button-gold leaderboard'
                    }
                    onClick={() => {
                        playButtonAudio();
                        if (!isLeaderboard() && eventConcluded()) {
                            dispatch(
                                promotionToolSliceActions.isOpenDialog(false)
                            );
                            dispatch(
                                popupSliceActions.open(
                                    'promotion_tool.popup_event_ended'
                                )
                            );
                            return;
                        }

                        dispatch(
                            promotionToolSliceActions.updatePage(
                                PromotionPage.Ranking
                            )
                        );
                    }}
                >
                    {t('promotion_tool.rankings')}
                </Button>
            </Stack>
            <Button
                id={`${page}_${isLeaderboard() ? 'le' : 're'}_popup_snooze`}
                className="snooze-button"
                onClick={() => show()}
            >
                <div className={getSnoozeButtonStyle(showToday)}>
                    {showToday && <EventChecked className="tick-icon" />}
                </div>
                <Typography
                    sx={{
                        marginLeft: '4px',
                        color: '#DCDCDC',
                        fontSize: '16px',
                    }}
                >
                    {t('promotion_tool.ignore')}
                </Typography>
            </Button>
        </>
    );
};
